import React, {memo, ReactNode, useContext, useMemo} from 'react'
import {invariant} from '../../helpers/invariant'
import {BuilderReferenceContent} from '../components/BuilderReferenceContent'

export const isAccountFormLabelsModel = (data: unknown): data is AccountFormLabelsModel => {
    if (typeof data !== 'object' || data === null) {
        return false
    }
    const maybeModel = data as AccountFormLabelsModel
    if (maybeModel.value === undefined) {
        return false
    }
    if (maybeModel.value && typeof maybeModel.value !== 'object') {
        return false
    }
    if (maybeModel.value?.data?.login && typeof maybeModel.value.data.login !== 'object') {
        return false
    }
    if (
        maybeModel.value?.data?.registration &&
        typeof maybeModel.value.data.registration !== 'object'
    ) {
        return false
    }
    return maybeModel['@type'] === '@builder.io/core:Reference'
}

interface ModelReference<Model = unknown> {
    id: string
    '@type': string
    model: string
    value?: {
        data: Model
    }
}

type AccountFormLabelsModel = ModelReference<{
    login: LoginFormLabels
    registration: RegistrationFormLabels
}>

interface LoginFormLabels {
    formTitle: ModelReference
    email: string
    password: string
    rememberMe: string
}

interface RegistrationFormLabels {
    formTitle?: ModelReference
    perksEnrollmentTerms?: ModelReference
    email: string
    confirmPassword: string
    password: string
    phone: string
    smsOptIn: string
}

interface AccountFormLabelsProviderProps {
    children: ReactNode
    data: AccountFormLabelsModel
}

export type {AccountFormLabelsModel, LoginFormLabels, RegistrationFormLabels}

export const useGetAccountFormData = (componentFormLabels?: AccountFormLabelsModel) => {
    const accountFormLabelsContext = useAccountFormLabelsContext()
    const accountFormLabels = componentFormLabels || accountFormLabelsContext

    const formLabels = accountFormLabels?.value?.data
    const {login: loginFormLabels, registration: registrationFormLabels} = formLabels || {}

    const renderContent = (content: ModelReference | undefined, testId?: string) =>
        content && (
            <BuilderReferenceContent
                reference={content}
                testId={testId || 'useGetAccountFormData-Content'}
            />
        )

    return {
        formLabels,
        perksEnrollmentTermsContent: renderContent(
            registrationFormLabels?.perksEnrollmentTerms,
            'useGetAccountFormData-Content'
        ),
        loginTitleContent: renderContent(
            loginFormLabels?.formTitle,
            'useGetAccountFormData-Content'
        ),
        registrationTitleContent: renderContent(
            registrationFormLabels?.formTitle,
            'useGetAccountFormData-Content'
        )
    }
}

const AccountFormLabelsContext = React.createContext<AccountFormLabelsModel>(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    undefined!
)
export const useAccountFormLabelsContext = () => {
    const context = useContext(AccountFormLabelsContext)

    invariant(
        context,
        'useAccountFormLabelsContext must be used within an AccountFormLabelsProvider'
    )
    return context
}
export const AccountFormLabelsProvider = memo(
    ({children, data}: AccountFormLabelsProviderProps) => {
        invariant(data, 'AccountFormLabelsProvider requires data prop to be provided')
        useMemo(
            () =>
                invariant(
                    isAccountFormLabelsModel(data) === true,
                    'AccountFormLabelsContext is not valid AccountFormLabelsModel'
                ),
            [data]
        )
        return (
            <AccountFormLabelsContext.Provider value={data}>
                {children}
            </AccountFormLabelsContext.Provider>
        )
    }
)
AccountFormLabelsProvider.displayName = 'AccountFormLabelsProvider'
